/**
 * Full-page service ticket: Основное / Отчёт / История + chat rail.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import {
  Section,
  Field,
  CopyableField,
  CopyableFieldWithAction,
  FieldWithActions,
} from '../components/DetailDrawerContent';
import {
  formatTicketDate,
  isChecklistItemDone,
  shortTicketId,
  slaStatusLabel,
  stageLabel,
  diagnosisItems,
  technicianCommentBody,
  technicianOutcome,
  ticketDescriptionBody,
  ticketSerials,
  inventoryReportCount,
  ticketTitle,
  typeLabel,
} from './serviceTicketLabels';
import { getCurrentRole, isTechnician } from '../utils/accessControl';
import { setCrossLinkPowerbankId } from '../../modules/powerbanks/powerbanksAction';

function copyToClipboard(text, notifications, t) {
  if (!text || text === '—') return;
  navigator.clipboard.writeText(String(text)).then(
    () => notifications?.show?.(t('drawer.copySuccess') || t('drawer.copy'), { severity: 'success', autoHideDuration: 2000 }),
    () => notifications?.show?.(t('service.errorSave'), { severity: 'error', autoHideDuration: 3000 }),
  );
}

function AuthPhoto({ ticketId, photoId, label, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl;
    (async () => {
      try {
        const { data } = await api.getServiceTicketPhotoBlob(ticketId, photoId);
        objectUrl = URL.createObjectURL(data);
        if (alive) setUrl(objectUrl);
      } catch {
        if (alive) setUrl(null);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketId, photoId]);

  return (
    <Box
      role={url ? 'button' : undefined}
      tabIndex={url ? 0 : undefined}
      onClick={() => url && onOpen?.({ url, label })}
      onKeyDown={(e) => {
        if (url && (e.key === 'Enter' || e.key === ' ')) onOpen?.({ url, label });
      }}
      sx={{
        width: 140,
        height: 100,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: url ? 'pointer' : 'default',
      }}
    >
      {url ? (
        <Box component="img" src={url} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      )}
    </Box>
  );
}

function SerialSection({ title, count, serials, missingLabel, onOpen, onCopy, t, techUser }) {
  const n = Number(count);
  const hasCount = Number.isFinite(n) && n > 0;
  if (!hasCount && (!serials || serials.length === 0)) return null;
  return (
    <Section title={title}>
      {hasCount ? (
        <Typography variant="body2" sx={{ mb: serials.length ? 1 : 0 }}>
          {t('service.pcs', { count: n })}
        </Typography>
      ) : null}
      {serials.length > 0 ? (
        serials.map((sn) => (
          <CopyableFieldWithAction
            key={sn}
            label={t('Powerbanks')}
            value={sn}
            onCopy={(v) => onCopy(v)}
            copyTooltip={t('drawer.copy')}
            onOpen={techUser ? undefined : () => onOpen(sn)}
            openTooltip={t('service.openInPowerbanks')}
          />
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">{missingLabel}</Typography>
      )}
    </Section>
  );
}

export default function ServiceTicketDetailPage() {
  const { id: ticketId } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const notifications = useNotifications();
  const techUser = isTechnician(getCurrentRole());

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(0);
  const [technicians, setTechnicians] = useState([]);
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [comment, setComment] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [returnOpen, setReturnOpen] = useState(false);
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [stationSerial, setStationSerial] = useState('');

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.getServiceTicket(ticketId);
      setTicket(data);
      setAssigneeEmail(data.assigneeEmail || '');
      setEditingAssignee(!data.assigneeEmail);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || t('service.errorLoad'));
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!ticket?.ownerAdminEmail) {
      setTechnicians([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getServiceTechnicians({
          parentAdminEmail: ticket.ownerAdminEmail,
          active: true,
        });
        if (cancelled) return;
        setTechnicians(Array.isArray(data) ? data.filter((p) => p.active !== false) : []);
      } catch {
        if (!cancelled) setTechnicians([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ticket?.ownerAdminEmail]);

  useEffect(() => {
    const fromTicket = ticket?.stationId || ticket?.stationID;
    if (fromTicket) {
      setStationSerial(String(fromTicket));
      return undefined;
    }
    if (!ticket?.stationNumber) {
      setStationSerial('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.findStationByNumber(ticket.stationNumber);
        const raw = data?.station || data || {};
        const serial = (raw.stationID || raw.stationId || '').toString().trim();
        if (!cancelled) setStationSerial(serial);
      } catch {
        if (!cancelled) setStationSerial('');
      }
    })();
    return () => { cancelled = true; };
  }, [ticket?.stationId, ticket?.stationID, ticket?.stationNumber]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || t('service.errorSave'));
    } finally {
      setBusy(false);
    }
  };

  const p = ticket?.permissions || {};
  const checklist = ticket?.checklist || [];
  const boolItems = diagnosisItems(ticket);
  const photoItems = checklist.filter((item) => (
    item.type === 'PHOTO' && (item.id === 'photo_before' || item.id === 'photo_after')
  ));
  const addedItem = checklist.find((item) => item.id === 'pb_added_count');
  const removedItem = checklist.find((item) => item.id === 'pb_removed_count');
  const addedSerials = ticketSerials(ticket, 'added');
  const removedSerials = ticketSerials(ticket, 'removed');
  const boolDone = boolItems.filter((item) => isChecklistItemDone(item)).length;
  const shortId = shortTicketId(ticket?.id || ticketId);
  // OTHER may be station-less (e.g. warehouse); show equipment only when linked.
  const showEquipment = Boolean(ticket?.stationNumber);
  const online = String(ticket?.stationStatus || '').toUpperCase() === 'CONNECTED';
  const slaLine = ticket ? slaStatusLabel(t, ticket) : '';

  const assigneeChoices = useMemo(() => {
    const list = technicians.map((tech) => ({
      email: tech.email,
      label: tech.name ? `${tech.name} (${tech.email})` : tech.email,
    }));
    if (ticket?.ownerAdminEmail && !list.some((x) => x.email === ticket.ownerAdminEmail)) {
      list.push({
        email: ticket.ownerAdminEmail,
        label: `${ticket.ownerAdminEmail} (${t('service.partner')})`,
      });
    }
    return list;
  }, [technicians, ticket?.ownerAdminEmail, t]);

  const openStation = () => {
    if (techUser || !ticket?.stationNumber) return;
    try { sessionStorage.setItem('crossLinkStation', String(ticket.stationNumber)); } catch (_) { /* ignore */ }
    window.location.hash = '#/station';
  };
  const openPartner = () => {
    if (techUser || !ticket?.ownerAdminEmail) return;
    try { sessionStorage.setItem('crossLinkMerchant', String(ticket.ownerAdminEmail)); } catch (_) { /* ignore */ }
    window.location.hash = '#/merchants';
  };
  const openAssignee = (email) => {
    if (techUser || !email) return;
    try { sessionStorage.setItem('crossLinkTechnician', String(email)); } catch (_) { /* ignore */ }
    window.location.hash = '#/service/technicians';
  };
  const openPowerbank = (pbId) => {
    if (techUser || !pbId) return;
    dispatch(setCrossLinkPowerbankId(pbId));
    window.location.hash = '#/powerBank';
  };

  const outcomeTitle = technicianOutcome(ticket?.comments)
    || (ticket?.stage === 'CLOSED' && ticket?.type === 'OFFLINE' ? t('service.connectionOnline') : null)
    || (ticket?.stage === 'DONE' || ticket?.stage === 'CLOSED' ? t('service.complete') : null);

  const reviewHistory = [...(ticket?.history || [])]
    .reverse()
    .find((h) => h.toStage === 'CLOSED' || /Review PASS/i.test(h.note || ''));

  const goBackToList = () => history.push('/service/tickets');

  const approveTicket = () => run(() => api.reviewServiceTicket(ticketId, {
    decision: 'PASS',
    paymentStatus: 'APPROVED_FOR_PAY',
  }));

  const returnTicket = () => run(async () => {
    await api.reviewServiceTicket(ticketId, {
      decision: 'FAIL',
      comment: reviewComment.trim(),
    });
    setReturnOpen(false);
    setReviewComment('');
  });

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('service.ticketsTitle'), path: '/service/tickets' },
        { title: shortId },
      ]}
    >
      {loading && !ticket && (
        <Typography variant="body2" color="text.secondary">…</Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {ticket && (
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={goBackToList}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('service.back')}
          </Button>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h4">{t('service.ticket')} {shortId}</Typography>
              <Chip size="small" label={typeLabel(t, ticket.type)} />
              <Chip
                size="small"
                label={stageLabel(t, ticket.stage)}
                color={ticket.stage === 'DONE' ? 'success' : ticket.overdue ? 'error' : 'default'}
                variant={ticket.stage === 'DONE' ? 'filled' : 'outlined'}
              />
              {slaLine ? (
                <Chip size="small" label={slaLine} color="error" variant="outlined" />
              ) : null}
            </Stack>
            {p.canReview && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" disabled={busy} onClick={() => setReturnOpen(true)}>
                  {t('service.reject')}
                </Button>
                <Button variant="contained" disabled={busy} onClick={approveTicket}>
                  {t('service.approve')}
                </Button>
              </Stack>
            )}
          </Stack>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, minHeight: 40 }}>
                <Tab label={t('service.tabMain')} />
                <Tab label={t('service.tabReport')} />
                <Tab label={t('service.tabHistory')} />
              </Tabs>

              {tab === 0 && (
                <Stack spacing={0}>
                  <Section title={t('service.ticket')}>
                    <CopyableField
                      label={t('service.ticketId')}
                      value={ticket.id}
                      onCopy={(v) => copyToClipboard(v, notifications, t)}
                      copyTooltip={t('drawer.copy')}
                    />
                    <Field label={t('service.colCreated')} value={formatTicketDate(ticket.createdAt)} />
                    <Field
                      label={t('service.colSla')}
                      value={(
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{formatTicketDate(ticket.slaDueAt)}</Typography>
                          {slaLine ? (
                            <Chip
                              size="small"
                              label={slaLine}
                              color="error"
                              variant="outlined"
                              sx={{ alignSelf: 'flex-start', height: 20 }}
                            />
                          ) : null}
                        </Stack>
                      )}
                    />
                    <CopyableFieldWithAction
                      label={t('service.partner')}
                      value={ticket.ownerAdminEmail}
                      onCopy={(v) => copyToClipboard(v, notifications, t)}
                      copyTooltip={t('drawer.copy')}
                      onOpen={techUser ? undefined : openPartner}
                      openTooltip={techUser ? undefined : t('service.openInMerchants')}
                    />
                    {p.canAssign && (editingAssignee || !ticket.assigneeEmail) ? (
                      <Box sx={{ py: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('service.colAssignee')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
                          <TextField
                            select
                            size="small"
                            label={t('service.selectAssignee')}
                            value={assigneeEmail}
                            onChange={(e) => setAssigneeEmail(e.target.value)}
                            disabled={busy || assigneeChoices.length === 0}
                            sx={{ minWidth: 240, flex: 1 }}
                          >
                            {assigneeChoices.map((c) => (
                              <MenuItem key={c.email} value={c.email}>{c.label}</MenuItem>
                            ))}
                          </TextField>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy || !assigneeEmail}
                            onClick={() => run(async () => {
                              await api.assignServiceTicket(ticketId, assigneeEmail);
                              setEditingAssignee(false);
                            })}
                          >
                            {t('service.assign')}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <FieldWithActions
                        label={t('service.colAssignee')}
                        value={ticket.assigneeEmail}
                        onCopy={(v) => copyToClipboard(v, notifications, t)}
                        copyTooltip={t('drawer.copy')}
                        actionButton={(
                          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                            {!techUser && ticket.assigneeEmail ? (
                              <Tooltip title={t('service.openInTechnicians')}>
                                <IconButton size="small" aria-label={t('service.openInTechnicians')} onClick={() => openAssignee(ticket.assigneeEmail)}>
                                  <OpenInNewOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {p.canAssign && ticket.stage !== 'CLOSED' && (
                              <Button size="small" variant="text" onClick={() => setEditingAssignee(true)} sx={{ minWidth: 0, px: 1 }}>
                                {t('service.changeAssignee')}
                              </Button>
                            )}
                          </Stack>
                        )}
                      />
                    )}
                    <Field label={ticket.type === 'OTHER' ? t('service.taskTitle') : t('service.task')} value={ticketTitle(ticket) || '—'} />
                    {ticket.type === 'OTHER' && ticketDescriptionBody(ticket) ? (
                      <Field label={t('service.description')} value={ticketDescriptionBody(ticket)} />
                    ) : null}
                  </Section>

                  {showEquipment && (
                    <Section title={t('service.equipment')}>
                      <CopyableFieldWithAction
                        label={t('service.stationNumber')}
                        value={ticket.stationNumber}
                        onCopy={(v) => copyToClipboard(v, notifications, t)}
                        copyTooltip={t('drawer.copy')}
                        onOpen={techUser ? undefined : openStation}
                        openTooltip={techUser ? undefined : t('service.openInStations')}
                      />
                      <CopyableField
                        label={t('service.equipmentSerial')}
                        value={stationSerial || ticket.stationId || ticket.stationID}
                        onCopy={(v) => copyToClipboard(v, notifications, t)}
                        copyTooltip={t('drawer.copy')}
                      />
                      <CopyableField
                        label={t('service.venue')}
                        value={ticket.venueName}
                        onCopy={(v) => copyToClipboard(v, notifications, t)}
                        copyTooltip={t('drawer.copy')}
                      />
                      <CopyableField
                        label={t('service.address')}
                        value={ticket.address}
                        onCopy={(v) => copyToClipboard(v, notifications, t)}
                        copyTooltip={t('drawer.copy')}
                      />
                      <Box sx={{ py: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('service.connection')}</Typography>
                        <Chip
                          size="small"
                          label={online ? t('service.connectionOnline') : t('service.connectionOffline')}
                          color={online ? 'success' : 'error'}
                        />
                      </Box>
                    </Section>
                  )}
                </Stack>
              )}

              {tab === 1 && (
                <Stack spacing={0}>
                  {ticket.stage === 'CLOSED' && reviewHistory && (
                    <Section title={t('service.reviewResult')}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {t('service.reviewAccepted')} · {formatTicketDate(reviewHistory.at)} · {reviewHistory.by || '—'}
                      </Typography>
                      {outcomeTitle && (
                        <Typography variant="body2" color="text.secondary">{outcomeTitle}</Typography>
                      )}
                    </Section>
                  )}

                  <Section title={t('service.outcome')}>
                    <Typography variant="subtitle1">{outcomeTitle || '—'}</Typography>
                    {ticket.stage === 'DONE' || ticket.stage === 'CLOSED' ? (
                      <Typography variant="body2" color="text.secondary">
                        {ticket.type === 'OFFLINE'
                          ? t('service.connectionOnline')
                          : t('service.complete')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </Section>

                  {boolItems.length > 0 && (
                    <Section title={`${t('service.diagnosis')} (${boolDone}/${boolItems.length})`}>
                      <Stack spacing={0.75}>
                        {boolItems.map((item) => {
                          const done = isChecklistItemDone(item);
                          return (
                            <Typography key={item.id} variant="body2" color={done ? 'success.main' : 'text.secondary'}>
                              {done ? '✓ ' : '○ '}{item.title}
                            </Typography>
                          );
                        })}
                      </Stack>
                    </Section>
                  )}

                  <SerialSection
                    title={t('service.unloadedFromStation')}
                    count={inventoryReportCount(removedItem?.value, removedSerials)}
                    serials={removedSerials}
                    missingLabel={t('service.serialsMissing')}
                    onOpen={openPowerbank}
                    onCopy={(v) => copyToClipboard(v, notifications, t)}
                    t={t}
                    techUser={techUser}
                  />
                  <SerialSection
                    title={t('service.loadedIntoStation')}
                    count={inventoryReportCount(addedItem?.value, addedSerials)}
                    serials={addedSerials}
                    missingLabel={t('service.serialsMissing')}
                    onOpen={openPowerbank}
                    onCopy={(v) => copyToClipboard(v, notifications, t)}
                    t={t}
                    techUser={techUser}
                  />

                  <Section title={t('service.photoReport')}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {['photo_before', 'photo_after'].map((itemId) => {
                        const item = photoItems.find((x) => x.id === itemId) || checklist.find((x) => x.id === itemId);
                        const label = itemId === 'photo_before' ? t('service.photoBefore') : t('service.photoAfter');
                        const photoId = item?.photoIds?.[0];
                        return photoId ? (
                          <AuthPhoto
                            key={itemId}
                            ticketId={ticketId}
                            photoId={photoId}
                            label={label}
                            onOpen={setPhotoPreview}
                          />
                        ) : (
                          <Box
                            key={itemId}
                            sx={{
                              width: 140,
                              height: 100,
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Section>

                  <Section title={t('service.technicianComment')}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {technicianCommentBody(ticket.comments) || '—'}
                    </Typography>
                  </Section>
                </Stack>
              )}

              {tab === 2 && (
                <Section title={t('service.history')}>
                  <Stack spacing={1}>
                    {(ticket.history || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                    {[...(ticket.history || [])].slice().reverse().map((h, i) => (
                      <Box key={`${h.at}-${i}`} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatTicketDate(h.at)} · {h.by || '—'}
                        </Typography>
                        <Typography variant="body2">
                          {stageLabel(t, h.fromStage)} → {stageLabel(t, h.toStage)}
                          {h.note ? ` · ${h.note}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Section>
              )}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                width: { xs: '100%', lg: 360 },
                flexShrink: 0,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                minHeight: { lg: 520 },
                maxHeight: { lg: 'calc(100vh - 180px)' },
                position: { lg: 'sticky' },
                top: { lg: 16 },
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('service.chat')}</Typography>
              <Stack spacing={1} sx={{ flex: 1, overflow: 'auto', mb: 1.5 }}>
                {(ticket.comments || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">{t('service.chatEmpty')}</Typography>
                )}
                {(ticket.comments || []).map((c, i) => (
                  <Box key={`${c.at}-${i}`}>
                    <Typography variant="caption" color="text.secondary">
                      {c.by || '—'} · {formatTicketDate(c.at)}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('service.chatPlaceholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={busy || !p.canComment}
                />
                <Button
                  variant="contained"
                  disabled={busy || !p.canComment || !comment.trim()}
                  onClick={() => run(async () => {
                    await api.commentServiceTicket(ticketId, comment.trim());
                    setComment('');
                  })}
                >
                  {t('service.chatSend')}
                </Button>
              </Stack>
            </Paper>
          </Box>
        </Stack>
      )}

      <Dialog open={returnOpen} onClose={() => !busy && setReturnOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('service.reviewReturnTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            label={t('service.reviewReturnHint')}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={busy || !reviewComment.trim()} onClick={returnTicket}>
            {t('service.reviewReturnConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(photoPreview)} onClose={() => setPhotoPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>{photoPreview?.label || t('service.photoOpen')}</DialogTitle>
        <DialogContent>
          {photoPreview?.url ? (
            <Box component="img" src={photoPreview.url} alt={photoPreview.label} sx={{ width: '100%', borderRadius: 1 }} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoPreview(null)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
